import { Analytics } from '@cobliteam/analytics';
import { useAssignDriverFaceImage } from '@cobliteam/hooks/src/useCamera/useDriverFaceCluster';
import { Alert, Button, Dialog, Typography } from '@cobliteam/ui/components';
import { Checkbox, Label } from '@cobliteam/ui/form';
import Icon from '@cobliteam/ui/icons';
import { useEffect, useState } from 'react';

const reasons = {
  NOT_A_DRIVER: 'Não é um motorista',
  INCORRECT_DRIVER: 'Motorista incorreto',
  BLURRY_OR_LOW_QUALITY: 'Imagem desfocada ou com baixa qualidade',
  OFFENSIVE_IMAGE: 'Foto ofensiva',
};

interface DriverImageRemoveDialogProps {
  imageUrl: string;
  removeImage: (image: string) => void;
}

const DriverImageRemoveDialog = ({ imageUrl, removeImage }: DriverImageRemoveDialogProps) => {
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);

  const { mutate: assignDriverFaceImage, isError, isSuccess } = useAssignDriverFaceImage();

  const handleSelectReason = (reason: string, checked: boolean | 'indeterminate') => {
    if (checked) {
      setSelectedReasons((prev) => [...prev, reason]);
      return;
    }

    setSelectedReasons((prev) => prev.filter((r) => r !== reason));
  };

  const handleRemoveImage = (skipAnalytics?: boolean) => {
    if (!skipAnalytics) {
      Analytics.track('Driver Face Cluster - Image - Remove', {
        reasons: selectedReasons,
      });
    }

    setSelectedReasons([]);

    const CDN_BASE_URL = 'https://d5c4nsv15lvpd.cloudfront.net/';
    const faceImageUrl = imageUrl.replace(CDN_BASE_URL, '').replace('_cropped', '').split('?')[0];

    assignDriverFaceImage([{ driverId: null, url: faceImageUrl }]);
  };

  useEffect(() => {
    if (isError) {
      Alert.error('Erro ao remover foto');
    }

    if (isSuccess) {
      Alert.success('Foto removida com sucesso');
      removeImage(imageUrl);
    }
  }, [isError, isSuccess, removeImage, imageUrl]);

  return (
    <Dialog>
      <Dialog.Trigger asChild>
        <Button
          variant='outlined'
          color='default'
          className='absolute bottom-2 left-4 z-10 bg-black-a6 text-white hover:bg-black-a7 hover:text-white'
          startIcon={<Icon name='trash-2' />}
        >
          Remover foto
        </Button>
      </Dialog.Trigger>

      <Dialog.Content size='sm'>
        <Dialog.Header>
          <Dialog.Title>Remover foto</Dialog.Title>
          <Dialog.Description className='sr-only' />
          <Dialog.Close />
        </Dialog.Header>

        <Dialog.Body className='flex flex-col gap-4'>
          <Typography variant='body'>Por que a foto está incorreta?</Typography>

          <div className='flex flex-col gap-1'>
            {Object.entries(reasons).map(([key, reason]) => (
              <div key={key} className='flex items-center gap-2'>
                <Checkbox id={key} checked={selectedReasons.includes(key)} onCheckedChange={(checked) => handleSelectReason(key, checked)} />
                <Label htmlFor={key}>{reason}</Label>
              </div>
            ))}
          </div>
        </Dialog.Body>

        <Dialog.Footer className='flex items-center justify-end gap-2'>
          <Dialog.Close asChild>
            <Button variant='ghost' className='w-24' onClick={() => handleRemoveImage(true)}>
              Pular
            </Button>
          </Dialog.Close>
          <Dialog.Close asChild>
            <Button className='w-24' disabled={selectedReasons.length === 0} onClick={() => handleRemoveImage(false)}>
              Enviar
            </Button>
          </Dialog.Close>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};

export default DriverImageRemoveDialog;
